/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import { Pipe, PipeTransform } from '@angular/core';
import { ModelProperty } from '../engine/model-property';
import { MaskPipe } from './mask.pipe';
import { NameValue } from '../tool/name-value';
import { IEntity } from '../interface/IEntity';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

if (dayjs) {
  dayjs.extend(localizedFormat);
}

@Pipe({
  name: 'format',
  standalone: true,
})
export class FormatPipe implements PipeTransform {
  /**
   * Format Entity to Icon display (use font-awesome)
   * @param value Entity to convert
   * @param model of data
   */
  public static iconFactory(value: IEntity, model: ModelProperty): string {
    let icon;
    let textClass = null;
    if (model.type === 'boolean') {
      icon =
        model.formatter.values[value[model.formatter.field] ? 'true' : 'false'];
    } else {
      if (model.getPrimaryKey()) {
        const key = value[model.formatter.field][model.getPrimaryKey()];
        icon = model.formatter.values[key];
        if (model.formatter.displayText && !!model.formatter.textValues) {
          textClass = model.formatter.textValues[key];
        }
      } else {
        icon = model.formatter.values[value[model.formatter.field] + ''];
        if (model.formatter.displayText && !!model.formatter.textValues) {
          textClass =
            model.formatter.textValues[value[model.formatter.field] + ''];
        }
      }
    }
    if (!icon && model.formatter.defaultValue) {
      icon = model.formatter.defaultValue;
    }
    let text;
    if (model.formatter.displayText) {
      text = new FormatPipe().transform(value, model, true);
    }
    if (text) {
      text = `<span class="${textClass ? 'txt-' + textClass : ''} cell-align-${model.align}">${text}</span>`;
    }

    if (icon) {
      if (text) {
        return `<div class="commons-cell-format"><span class="fal table-icon fa-${icon}"></span> ${text}</div>`;
      } else {
        text = new FormatPipe().transform(value, model, true)
        return `<span class="fal table-icon fa-${icon}" data-search="${text}" title="${text}"></span>`;
      }
    } else if (text) {
      return `${text}`;
    } else {
      return ' - ';
    }
  }

  /**
   * Format Entity to color display
   * @param value Entity to convert
   * @param model of data
   */
  public static colorFactory(value: IEntity, model: ModelProperty): string {
    let classes;
    if (model.type === 'boolean') {
      classes =
        model.formatter.values[value[model.formatter.field] ? 'true' : 'false'];
    } else {
      classes = model.formatter.values[value[model.formatter.field] + ''];
    }
    return `<span class="${classes}">${MaskPipe.applyMask(
      value[model.id],
      model,
    )}</span>`;
  }

  public static linkFactory(value: IEntity, model: ModelProperty): string {
    let str = '';
    if (value[model.id] instanceof Array) {
      if (value[model.id].length > 0) {
        str += value[model.id]
          .map((v) => `<a class="commons-link"> ${v} </a>`)
          .join(', ');
      } else {
        return '-';
      }
    } else if (value[model.id]) {
      str += `<a class="commons-link"> ${value[model.id]} </a>`;
    } else {
      return '-';
    }
    if (model.formatter.icon && str) {
      str =
        `<span class="fal table-icon fa-${model.formatter.icon}"></span>` + str;
    }
    return str;
  }

  public static roundFactory(value: IEntity, model: ModelProperty) {
    if (value[model.formatter.field] && value[model.formatter.field].toFixed) {
      return value[model.formatter.field].toFixed(model.formatter.nbDecimal);
    } else {
      return value[model.formatter.field];
    }
  }

  public static gmapsFactory(value: IEntity, model: ModelProperty): string {
    const latField = model.formatter.lat;
    const lngField = model.formatter.lng;
    const lat = Number(value[latField]);
    const lng = Number(value[lngField]);
    const icon = model.formatter.icon
      ? model.formatter.icon
      : 'map-location-dot';

    if (isNaN(lat) || isNaN(lng)) {
      return '-';
    }
    if (lat === 0 && lng === 0) {
      return '-';
    }
    const style = 'color: #4fc1e9 !important; font-size: 17px';
    const result =
      `<a href="https://www.google.com/maps/place/${value[latField]},${value[lngField]}" class="gmaps-link" style="${style}" target="_blank">` +
      `<i class="fal fa-${icon}"></i>` +
      `</a>`;
    return result;
  }

  /**
   * Format standard Entity object to elegant display
   * @param value data with identified id and label on its model
   * @param model of data
   * @param tooltip set true to force full display
   */
  public static objectFactory(
    value: any,
    model: ModelProperty,
    tooltip = false,
  ): string {
    if (!value) {
      return '-';
    }
    let mainLib: string;
    let primaryKey: string;
    const text = value.toString();
    if (value instanceof NameValue) {
      if (model.displayId && model.displayLabel) {
        return value.toString();
      } else if (model.displayLabel) {
        return value.value;
      } else if (model.displayId) {
        return value.name;
      } else {
        return '-';
      }
    }
    if (text !== '[object Object]') {
      return value.toString();
    }
    for (const property in model.properties) {
      if (
        (tooltip || model.displayLabel) &&
        model.properties[property].mainLib
      ) {
        mainLib = value[property];
      } else if (
        (tooltip || model.displayId) &&
        model.properties[property].primaryKey
      ) {
        primaryKey = value[property];
      }
    }
    if (mainLib) {
      if (primaryKey) {
        return `${primaryKey} - ${mainLib}`;
      } else {
        return mainLib;
      }
    } else if (primaryKey) {
      return primaryKey;
    } else {
      return '-';
    }
  }

  /**
   * Convert array of primitive or object value to elegant display
   */
  public static arrayFactory(value: any[], model: ModelProperty) {
    if (model.getPrimaryKey() || model.getLabelKey()) {
      const array = [];
      for (const v of value) {
        array.push(this.objectFactory(v, model));
      }
      return array;
    } else {
      return value;
    }
  }

  /**
   * Centralisation of conversion
   */
  transform(
    value: IEntity,
    model?: ModelProperty,
    notApplyFormatter?: boolean,
  ): any {
    if (!model.formatter || (notApplyFormatter && value[model.id])) {
      // Default Formatter
      if (value[model.id] instanceof Array) {
        if (value[model.id].length > 0) {
          return FormatPipe.arrayFactory(value[model.id], model);
        } else {
          return '-';
        }
      } else if (value[model.id] instanceof Date) {
        return dayjs(value[model.id]).format('DD/MM/YYYY HH:mm:ss');
      } else if (typeof value[model.id] === 'object') {
        return MaskPipe.applyMask(
          FormatPipe.objectFactory(value[model.id], model),
          model,
        );
      } else {
        return MaskPipe.applyMask(value[model.id], model);
      }
    } else if (!notApplyFormatter) {
      switch (
        model.formatter.type // Classic Formatter
      ) {
        case 'icon':
          return FormatPipe.iconFactory(value, model);
        case 'color':
          return MaskPipe.applyMask(
            FormatPipe.colorFactory(value, model),
            model,
          );
        case 'link':
          return FormatPipe.linkFactory(value, model);
        case 'round':
          return FormatPipe.roundFactory(value, model);
      }
    }
  }
}
